import React from 'react';

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};

const EpisodeItem = ({ ep, onItemClick }: { ep: Record<string, any>; onItemClick?: ((id: unknown) => void) | null }) => {
  const inner = (
    <>
      <div className="pco-episode-play">▶</div>
      <div className="pco-episode-info">
        <h4>{ep.title}</h4>
        <div className="pco-ep-meta">
          {ep.speaker && <>{ep.speaker} · </>}
          {formatDate(ep.published_at)}
        </div>
      </div>
    </>
  );

  if (onItemClick) {
    return (
      <div className="pco-episode-item pco-episode-clickable" onClick={() => onItemClick(ep.id)} key={ep.id}>
        {inner}
      </div>
    );
  }
  return <div className="pco-episode-item" key={ep.id}>{inner}</div>;
};

const LatestSeries = ({ series, episodes, onItemClick }: { series: Record<string, any>[]; episodes: Record<string, any>[]; onItemClick?: ((id: unknown) => void) | null }) => {
  const latest = series[0];
  if (!latest) return <div className="pco-empty"><p>No series available.</p></div>;
  const latestEps = episodes.filter((ep: Record<string, any>) => ep.series_id === latest.id);

  return (
    <div className="pco-latest-series">
      <div className="pco-series-hero">
        {latest.artwork_url && (
          <img className="pco-series-hero-image" src={latest.artwork_url} alt={latest.title} loading="lazy" />
        )}
        <div className="pco-series-hero-body">
          <h3>{latest.title}</h3>
          {latest.description && <p>{latest.description}</p>}
          <div className="pco-series-hero-meta">{latest.episode_count || latestEps.length} messages</div>
        </div>
      </div>
      {latestEps.length > 0 && (
        <div className="pco-episodes-list">
          {latestEps.map((ep: Record<string, any>) => <EpisodeItem ep={ep} onItemClick={onItemClick} key={ep.id} />)}
        </div>
      )}
    </div>
  );
};

const AllSeries = ({ series }: { series: Record<string, any>[] }) => {
  if (!series.length) return <div className="pco-empty"><p>No series available.</p></div>;
  return (
    <div className="pco-series-grid">
      {series.map((s: Record<string, any>) => (
        <div className="pco-series-card" key={s.id}>
          {s.artwork_url ? (
            <img className="pco-series-card-image" src={s.artwork_url} alt={s.title} loading="lazy" />
          ) : (
            <div className="pco-series-card-image" />
          )}
          <div className="pco-series-card-body">
            <h3>{s.title}</h3>
            {s.description && <p>{s.description.substring(0, 120)}</p>}
            <div className="pco-ep-count">{s.episode_count} messages</div>
          </div>
        </div>
      ))}
    </div>
  );
};

const RecentMessages = ({ series, episodes, onItemClick }: { series: Record<string, any>[]; episodes: Record<string, any>[]; onItemClick?: ((id: unknown) => void) | null }) => {
  if (!episodes.length) return <div className="pco-empty"><p>No messages available.</p></div>;
  return (
    <div className="pco-recent-messages">
      <div className="pco-episodes-list">
        {episodes.slice(0, 10).map((ep: Record<string, any>) => {
          const seriesObj = series.find((s: Record<string, any>) => s.id === ep.series_id);
          return (
            <EpisodeItem
              ep={{ ...ep, speaker: [ep.speaker, seriesObj ? seriesObj.title : ''].filter(Boolean).join(' · ') }}
              onItemClick={onItemClick}
              key={ep.id}
            />
          );
        })}
      </div>
    </div>
  );
};

const Sermons = ({ data, settings = {} as Record<string, any>, onItemClick }: { data: Record<string, any>; settings?: Record<string, any>; onItemClick?: ((id: unknown) => void) | null }) => {
  // data can be:
  // - Array of series with nested episodes (from API)
  // - Object with { series, episodes } (from Studio data-pco-json)
  let series, episodes;
  if (Array.isArray(data)) {
    series = data;
    episodes = data.reduce((acc, s) => acc.concat(s.episodes || []), []);
  } else {
    series = (data && data.series) || [];
    episodes = (data && data.episodes) || [];
  }

  if (!series.length && !episodes.length) {
    return <div className="pco-empty"><p>No series available at this time.</p></div>;
  }

  const display = settings['pco-display'] || 'latest-series';
  const clickHandler = settings._studioMode ? null : onItemClick;

  switch (display) {
    case 'all-series':
      return <AllSeries series={series} />;
    case 'recent-messages':
      return <RecentMessages series={series} episodes={episodes} onItemClick={clickHandler} />;
    default:
      return <LatestSeries series={series} episodes={episodes} onItemClick={clickHandler} />;
  }
};

export default Sermons;
